/* global google */
import React, { useEffect, useState, useRef } from 'react';
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import { getDatabase, ref, get, child } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import AddressSearch from './AddressSearch';
import {
  getApplicableTariff,
  calculateFare,
  LARGE_PARTY_SURCHARGE,
  LARGE_PARTY_THRESHOLD,
} from './fareCalculator';
import './RiderMap.css';
import { OverlayView } from '@react-google-maps/api';


const isInCoreServiceArea = (lat, lng) => {
  // Beverley town center
  if (lat >= 53.820 && lat <= 53.870 && lng >= -0.470 && lng <= -0.390) {
    return true;
  }
  
  // Woodmansey area (south of Beverley)
  if (lat >= 53.815 && lat <= 53.820 && lng >= -0.405 && lng <= -0.380) {
    return true;
  }
  
  // Leconfield area (northeast of Beverley)
  if (lat >= 53.860 && lat <= 53.930 && lng >= -0.460 && lng <= -0.390) {
    return true;
  }
  
  // Cherry Burton area (southwest of Beverley)
  if (lat >= 53.810 && lat <= 53.840 && lng >= -0.510 && lng <= -0.470) {
    return true;
  }
  
  // Walkington area (west of Beverley)
  if (lat >= 53.830 && lat <= 53.860 && lng >= -0.520 && lng <= -0.480) {
    return true;
  }

  // Bishop Burton area (south of Beverley)
  if (lat >= 53.800 && lat <= 53.830 && lng >= -0.450 && lng <= -0.410) {
    return true;
  }

  // dunswell area (northwest of Beverley)
  if (lat >= 53.830 && lat <= 53.860 && lng >= -0.410 && lng <= -0.370) {
    return true;
  }

  return false;
};

const isInExtendedPickupArea = (lat, lng) => {
  // Wider East Riding area for pickups (includes Market Weighton, etc.)
  // Market Weighton area
  if (lat >= 53.850 && lat <= 53.890 && lng >= -0.670 && lng <= -0.630) {
    return true;
  }
  
  // General East Riding pickup area (more reasonable size)
  if (lat >= 53.7 && lat <= 54.0 && lng >= -0.8 && lng <= -0.2) {
    return true;
  }
  
  return false;
};

const isValidTrip = (pickupLat, pickupLng, dropoffLat, dropoffLng) => {
  const pickupInCore = isInCoreServiceArea(pickupLat, pickupLng);
  const dropoffInCore = isInCoreServiceArea(dropoffLat, dropoffLng);
  const pickupInExtended = isInExtendedPickupArea(pickupLat, pickupLng);
  
  // Business rules:
  // 1. Core to Core: Always allowed (local trips)
  // 2. Core to anywhere: Always allowed (going out from your area)
  // 3. Extended pickup to Core: Allowed (coming into your area - good business!)
  // 4. Extended to Extended: Not allowed (short trips outside your area)
  
  if (pickupInCore || dropoffInCore) {
    return true; // At least one end is in core service area
  }

  if (pickupInCore) {
    return true; 
  }

  if (pickupInExtended && dropoffInCore) {
    return true; // Pickup from wider area coming to your service area
  }
  
  return false; // Both outside core area
};

const RiderMap = () => {
  const [userName, setUserName] = useState('');
  const [phone, setPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [pickupLatLng, setPickupLatLng] = useState(null);
  const [dropoffLatLng, setDropoffLatLng] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [routePath, setRoutePath] = useState([]);
  const [tripTime, setTripTime] = useState(null);
  const [distanceMiles, setDistanceMiles] = useState(null);
  const [fare, setFare] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [bookingMode, setBookingMode] = useState('asap');
  const [scheduledTime, setScheduledTime] = useState('');
  const [numberOfRiders, setNumberOfRiders] = useState(1);
  const [wheelchairRequired, setWheelchairRequired] = useState(false);

  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyBKQaifAZ7DdD62JkJXq8lBsHxHPr-mGxw',
    libraries: ['places', 'geometry'],
  });

  useEffect(() => {
    const type = localStorage.getItem('userType');
    const uid = localStorage.getItem('uid');
    const dbRef = ref(getDatabase());

    if (type === 'rider') {
      get(child(dbRef, `Users/${uid}`)).then((snapshot) => {
        const data = snapshot.val();
        setUserName(data?.name || '');
        setPhone(data?.phone || '');
        setUserEmail(data?.email || '');
      });
    } else if (type === 'guest') {
      setUserName(localStorage.getItem('guestName') || '');
      setPhone(localStorage.getItem('guestPhone') || '');
      setUserEmail(localStorage.getItem('guestEmail') || '');
    }
  }, []);

  useEffect(() => {
    if (!pickupLatLng || !dropoffLatLng) {
      setRoutePath([]);
      setTripTime(null);
      setDistanceMiles(null);
      setFare(null);
      setSelectedTariff(null);

      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }

      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: pickupLatLng,
        destination: dropoffLatLng,
        travelMode: 'DRIVING',
      },
      (result, status) => {
        if (status === 'OK' && result.routes.length) {
          const route = result.routes[0];
          const leg = route.legs[0];

          setRoutePath(route.overview_path);
          setTripTime(leg.duration?.value / 60);
          const yards = leg.distance?.value * 1.09361;
          setDistanceMiles(leg.distance?.value / 1609.34);

          const targetTime = bookingMode === 'scheduled' ? new Date(scheduledTime) : new Date();
          const tariff = getApplicableTariff(targetTime);
          setSelectedTariff(tariff);
          setFare(calculateFare(yards, tariff, { numberOfRiders }));

          const bounds = new google.maps.LatLngBounds();
          bounds.extend(pickupLatLng);
          bounds.extend(dropoffLatLng);
          mapRef.current.fitBounds(bounds, 80);
        }
      }
    );
  }, [pickupLatLng, dropoffLatLng, bookingMode, scheduledTime, numberOfRiders]);

  const handleNavigate = () => {
    const pickupLat = pickupLatLng?.lat;
    const pickupLng = pickupLatLng?.lng;
    const dropoffLat = dropoffLatLng?.lat;
    const dropoffLng = dropoffLatLng?.lng;

    if (pickupLat && pickupLng && dropoffLat && dropoffLng) {
      const isValid = isValidTrip(pickupLat, pickupLng, dropoffLat, dropoffLng);

      if (!isValid) {
        alert(
          'Sorry! We focus on trips to/from our core service area (Beverley and surrounding villages). ' +
          'We\'re happy to pick up from places outside of Beverley if you\'re coming to our area, ' +
          'but we don\'t do short trips outside Beverley.'
        );
        return;
      }
    }

    navigate('/tripDetails', {
      state: {
        pickup: pickupAddress,
        dropoff: dropoffAddress,
        pickupLatLng,
        dropoffLatLng,
        name: userName,
        phone,
        email: userEmail,
        distanceMiles,
        fare,
        tripTime,
        tariffName: selectedTariff?.name || 'Standard',
        bookingMode,
        scheduledTime: bookingMode === 'scheduled' ? scheduledTime : null,
        numberOfRiders,
        wheelchairRequired,
      },
    });
  };

  const getShortLabel = (address) => {
    if (!address) return '';
    const commaIndex = address.indexOf(',');
    return commaIndex !== -1 ? address.slice(0, commaIndex) : address;
  };

  const renderSpeechBubble = (position, text, type) => (
    <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <div className={`speech-bubble-horizontal ${type}`} title={text}>
        <div className="bubble-tail-horizontal" />
        <div className="bubble-content">
          <div className="bubble-title">{type === 'pickup' ? 'PICK-UP' : 'DROP-OFF'}</div>
          <div className="bubble-address">{text}</div>
        </div>
      </div>
    </OverlayView>
  );

  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <header className="header-title">Rider Request Map</header>

      <div style={{ display: 'flex', flex: 1 }}>
        <div className="sidebar">
          <img src="https://u-gotaxi.co.uk/react-widget/assets/logo_ugo_taxi.png" alt="UGO Logo" className="logo" />
          {userName && (
            <>
              <div className="welcome-box">Welcome, {userName}</div>

               {localStorage.getItem('userType') !== 'guest' && (
                 <button
                   onClick={() => navigate('/ride-history')}
                   style={{
                    marginTop: '1rem',
                    width: '110%',
                    padding: '15px',
                    backgroundColor: '#00ab86',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                 }}
               >
                 View Ride History
               </button>
            )}
         </>
       )}

          <div className="booking-options" style={{ marginTop: '1rem' }}>
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafa', borderRadius: '8px' }}>
              <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                When do you want your ride?
              </label>

              <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                <button
                  onClick={() => setBookingMode('asap')}
                  style={{
                    flex: 1,
                    backgroundColor: bookingMode === 'asap' ? '#00ab86' : '#ffffff',
                    color: bookingMode === 'asap' ? '#ffffff' : '#00ab86',
                    border: '2px solid #00ab86',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    padding: '10px',
                    cursor: 'pointer',
                  }}
                >
                  Ride Now
                </button>
                <button
                  onClick={() => setBookingMode('scheduled')}
                  style={{
                    flex: 1,
                    backgroundColor: bookingMode === 'scheduled' ? '#00ab86' : '#ffffff',
                    color: bookingMode === 'scheduled' ? '#ffffff' : '#0d9488',
                    border: '2px solid #00ab86',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    padding: '10px',
                    cursor: 'pointer',
                  }}
                >
                  Ride Later
                </button>
              </div>

              {bookingMode === 'scheduled' && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <label htmlFor="datetime" style={{ fontSize: '0.9rem', fontWeight: 500 }}>Pickup Time:</label>
                  <input
                    id="datetime"
                    type="datetime-local"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          <AddressSearch
            onSelect={(latLng, address) => {
              setPickupLatLng(latLng);
              setPickupAddress(address);
            }}
            placeholder="Pickup location"
          />
          <div style={{ height: '1rem' }} />
          <AddressSearch
            onSelect={(latLng, address) => {
              setDropoffLatLng(latLng);
              setDropoffAddress(address);
            }}
            placeholder="Dropoff location"
          />

          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label htmlFor="numberOfRiders" style={{ fontSize: '0.9rem', fontWeight: 500 }}>Number of Riders:</label>
            <select
              id="numberOfRiders"
              value={numberOfRiders}
              onChange={(e) => setNumberOfRiders(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value={1}>1 Person</option>
              <option value={2}>2 People</option>
              <option value={3}>3 People</option>
              <option value={4}>4 People</option>
              <option value={5}>5 People</option>
            </select>
          </div>

          <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <input
              id="wheelchairRequired"
              type="checkbox"
              checked={wheelchairRequired}
              onChange={(e) => setWheelchairRequired(e.target.checked)}
            />
            <label htmlFor="wheelchairRequired" style={{ fontSize: '0.9rem', fontWeight: 500 }}>
              Wheelchair accessible vehicle required
            </label>
          </div>

          {fare && (
            <div className="fare-summary">
              <p><strong>Fare:</strong> £{fare.toFixed(2)}</p>
              <p><strong>Distance:</strong> {distanceMiles.toFixed(2)} mi</p>
              <p><strong>Duration:</strong> {tripTime.toFixed(1)} mins</p>
              <p><strong>Tariff:</strong> {selectedTariff?.name}</p>
              {numberOfRiders > LARGE_PARTY_THRESHOLD && (
                <p><strong>Extra rider fee:</strong> £{LARGE_PARTY_SURCHARGE.toFixed(2)}</p>
              )}
              {wheelchairRequired && <p><strong>Vehicle:</strong> Wheelchair accessible</p>}
            </div>
          )}

          <button
            className="see-price-btn"
            disabled={!fare || !pickupAddress || !dropoffAddress}
            onClick={handleNavigate}
          >
            Continue Booking
          </button>
        </div>

        <div className="map-wrapper">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={pickupLatLng || { lat: 53.8459, lng: -0.4233 }}
            zoom={13}
            onLoad={(map) => (mapRef.current = map)}
           
          >
            {pickupLatLng && (
              <>
                <Marker
                  position={pickupLatLng}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: '#00ab86',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: '#ffffff',
                  }}
                />
                {renderSpeechBubble(pickupLatLng, getShortLabel(pickupAddress), 'pickup')}
              </>
            )}

            {dropoffLatLng && (
              <>
                <Marker
                  position={dropoffLatLng}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: '#d93025',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: '#ffffff',
                  }}
                />
                {renderSpeechBubble(dropoffLatLng, getShortLabel(dropoffAddress), 'dropoff')}
              </>
            )}

            {routePath.length > 1 && (
              <Polyline
                path={routePath}
                options={{ strokeColor: '#00ab86', strokeWeight: 4 }}
                onLoad={(polyline) => {
                  if (polylineRef.current) {
                    polylineRef.current.setMap(null);
                  }
                  polylineRef.current = polyline;
                }}
              />
            )}
          </GoogleMap>
        </div>
      </div>
    </div>
  );
};

export default RiderMap;